import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { msg } from '@lingui/core/macro';
import * as Sentry from '@sentry/tanstackstart-react';
import { createIsomorphicFn } from '@tanstack/react-start';
import { getRequestHeaders, setResponseStatus } from '@tanstack/react-start/server';
import { parse } from 'cookie-es';
import { OperationDefinitionNode, print } from 'graphql';

import { getClientTag } from '#features/app-update/utils/appVersion';
import { CLIENT_OUTDATED_CODE, isSchemaDriftError } from '#features/app-update/utils/schemaDrift';
import { reportServerClosed } from '#features/app-update/utils/statusFlags';
import { defaultLocale } from '#features/i18n/constants/locales';
import { i18n } from '#features/i18n/i18n';
import { getLocaleFromPath } from '#features/i18n/utils';
import { envConfig } from '#constants/envConfig';
import { localStorage } from '#utils/localStorage';
import { ApiError } from './ApiError';
import { rememberBanNotice } from './banNotice';
import { getAppCheckToken } from './appCheck';
import { getNativeToken } from './nativeAuth';

export const USER_NOT_AUTH_CODE = 'user.not.auth';
const USER_BANNED_CODE = 'user.banned';
const PERMISSION_DENIED_CODE = 'permission.denied';

/**
 * На SSR локаль берём из куки запроса. Через createIsomorphicFn, а не через `await import`
 * под флагом: import-protection в Start 1.17x запрещает `@tanstack/react-start/server` в любом
 * модуле, достижимом из клиентской точки входа, — и динамический импорт он тоже видит.
 * Серверная ветка (вместе с этими импортами) вырезается из клиентского бандла.
 */
const getRequestCookieLocale = createIsomorphicFn()
  .client((): string | undefined => undefined)
  .server((): string | undefined => parse(getRequestHeaders().get('cookie') ?? '').locale);

/**
 * Падение бэкенда на SSR обязано стать 503 у самого документа — тогда nginx подменит страницу
 * на maintenance.html. No-op на клиенте (там ответ уже отдан).
 */
const markSsrResponseUnavailable = createIsomorphicFn()
  .client(() => {})
  .server(() => setResponseStatus(503));

/**
 * Адрес человека, за которого сейчас ходит SSR.
 *
 * Серверная отрисовка идёт из нашего контейнера, поэтому все её запросы приходят в шлюз с одного
 * адреса. Любой лимит по IP при этом считает не людей, а нас самих: шестьдесят открытий
 * страницы-приглашения подряд глушили публичный лендинг всем сразу. Заголовок клиента передаём
 * дальше — за Cloudflare это `CF-Connecting-IP`, без неё `X-Real-IP` от nginx.
 *
 * На клиенте no-op: там заголовок ставит сам прокси, а подделать его из браузера нельзя и не нужно.
 */
const getForwardedClientIp = createIsomorphicFn()
  .client((): string | undefined => undefined)
  .server((): string | undefined => {
    try {
      const headers = getRequestHeaders();
      return headers.get('cf-connecting-ip') ?? headers.get('x-real-ip') ?? undefined;
    } catch {
      // Вне запроса заголовков нет: прогрев, фоновая задача, тест. Адрес просто не передаём.
      return undefined;
    }
  });

const getHeaders = async (): Promise<Record<string, string | null | undefined>> => {
  const reCaptchaToken = localStorage.getItem('reCaptchaToken');
  let locale = defaultLocale;

  if (envConfig.IN_SERVER) {
    locale = getRequestCookieLocale() ?? defaultLocale;
  } else {
    locale = getLocaleFromPath();
  }

  // Native shell has no auth cookie — send the JWT in the `Accesstoken` header
  // instead (that's the app's configured app.jwt.header-key; the gateway reads it
  // as a fallback after the cookie). Web keeps using the cookie and never sets
  // this. No-op / dead-code-eliminated on web.
  const nativeToken = await getNativeToken();

  // Подтверждение подлинности приложения вместо капчи: в WebView reCAPTCHA всегда видит бота,
  // поэтому шлюз, получив валидный App Check, капчу не спрашивает. В вебе всегда null и заголовок
  // не уходит (см. appCheck.ts).
  const appCheckToken = await getAppCheckToken();

  // Пусто на клиенте и почти всегда заполнено на SSR — см. getForwardedClientIp.
  const clientIp = envConfig.IN_SERVER ? getForwardedClientIp() : undefined;

  return {
    'tm-captcha': reCaptchaToken,
    ...(appCheckToken && { 'tm-app-check': appCheckToken }),
    ...(envConfig.IN_SERVER && {
      'Accept-Language': 'ru-RU,ru;q=0.9,en-US;q=0.8,en;q=0.7',
      ...(clientIp && { 'CF-Connecting-IP': clientIp }),
    }),
    ...(nativeToken && { Accesstoken: nativeToken }),
    'active-tab': (envConfig.IN_SERVER ? false : document.visibilityState === 'visible').toString(),
    // Версия клиента в каждом запросе. Раньше она доезжала до бэкенда единственным путём —
    // вместе с регистрацией push-токена, то есть меньше чем у 2% устройств и только на Android;
    // разложить аварию по версиям было нечем. Пишется в access-лог nginx ($http_tm_client).
    // Заголовок обязан быть в allowedHeaders шлюза, иначе cross-origin preflight его отвергнет.
    'tm-client': getClientTag(),
    Language: locale,
  };
};

export const gqlFetcher = async <TData, TVariables = unknown>(
  documentNode: TypedDocumentNode<TData, TVariables> | string,
  variables?: TVariables | null,
  options?: {
    url?: string;
    revalidate?: number | false;
    tags?: string[];
    headers?: Record<string, string | null>;
    /**
     * Пережить выгрузку страницы. Нужен ровно там, где запрос отправляют в `pagehide`:
     * обычный fetch браузер отменяет вместе с документом, и телеметрия закрытой вкладки —
     * то есть самого интересного сеанса — теряется целиком.
     */
    keepalive?: boolean;
    /**
     * Не шуметь в Sentry и в консоли. Для запросов, которые никто не ждёт и которые по природе
     * своей падают на плохой сети — телеметрия при закрытии вкладки. Каждое такое падение в
     * Sentry — это шум, скрывающий настоящие ошибки.
     */
    silent?: boolean;
  }
): Promise<TData> => {
  const headers = await getHeaders();
  const requestUrl =
    (options?.url ?? (envConfig.IN_DEV && envConfig.IN_BROWSER)) ? '/graphql' : envConfig.API_URL;
  const operationDefinition =
    typeof documentNode === 'string'
      ? undefined
      : documentNode.definitions?.find(
          (definition): definition is OperationDefinitionNode =>
            definition.kind === 'OperationDefinition'
        );
  const operationName = operationDefinition?.name?.value ?? 'anonymous';
  const query =
    typeof documentNode === 'string'
      ? documentNode
      : (documentNode.loc?.source?.body ?? print(documentNode));
  const requestStartedAt = Date.now();
  let res: Response;

  try {
    res = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
        ...options?.headers,
      },
      credentials: 'include',
      ...(options?.keepalive ? { keepalive: true } : {}),
      body: JSON.stringify({
        query,
        variables,
      }),
    });
  } catch (error) {
    const durationMs = Date.now() - requestStartedAt;

    Sentry.withScope((scope) => {
      scope.setTag('scope', 'gqlFetcher');
      scope.setTag('type', 'network');
      scope.setTag('graphql.operation', operationName);
      scope.setLevel('warning');
      scope.setContext('network', {
        url: requestUrl,
        method: 'POST',
        durationMs,
        online: envConfig.IN_BROWSER ? navigator.onLine : undefined,
        visibilityState: envConfig.IN_BROWSER ? document.visibilityState : undefined,
      });
      scope.setContext('graphql', {
        operationName,
      });
      if (!options?.silent) {
        Sentry.captureException(error);
      }
    });

    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }

    const isOffline = envConfig.IN_BROWSER && navigator.onLine === false;
    const offlineMessage = i18n._(
      msg({
        id: 'common:network.offline',
        message: 'No internet connection. Please check your network and try again.',
      })
    );
    const networkMessage = i18n._(
      msg({
        id: 'common:network.error',
        message: 'Network error. Please check your connection and try again.',
      })
    );
    throw new ApiError(isOffline ? offlineMessage : networkMessage, 0);
  }

  // A bare 401/403 never comes from our gateway: it answers auth failures with 200 and an
  // error in the body (see USER_NOT_AUTH_CODE below). So this is something in front of us —
  // a bot challenge, a WAF rule, a proxy — and it says nothing about the token. Left
  // untagged on purpose: callers must not treat it as a reason to sign the person out.
  if (res.status === 401 || res.status === 403) {
    throw new ApiError('Unauthorized', res.status);
  }

  // 502/504 = short upstream/CF blip, body is HTML — don't try to parse as JSON.
  // 503 = our nginx @graphql_maintenance (JSON body), thrown with same message so
  // queryClient retries all three transparently.
  if (res.status === 502 || res.status === 503 || res.status === 504) {
    // On the server, a down/updating backend must surface as a 503 on the SSR
    // response itself, so nginx (@frontend_maintenance) swaps the page for
    // maintenance.html. Set it in the loader phase, before the stream starts.
    // The thrown ApiError is preserved so the client can still retry transparently.
    markSsrResponseUnavailable();
    // 503 is the one status that means "we closed the door", not "something blipped": it comes
    // from our own nginx under the maintenance flag. The app polls /app/status.json rarely — at
    // launch and on return from background — so whoever is already inside would keep tapping
    // into errors until they restart it. Tell the version gate to go and look now; the gate
    // still decides on the status file, not on this signal.
    if (res.status === 503) {
      reportServerClosed();
    }
    const maintenanceMessage = i18n._(
      msg({
        id: 'common:server.maintenance',
        message: 'Server is updating. Please try again in a moment.',
      })
    );
    throw new ApiError(maintenanceMessage, res.status);
  }

  const data = (await res.json()) as {
    data: TData;
    errors?: {
      message: string;
      path?: (string | number)[];
      extensions?: {
        httpCode?: number;
        errorCode?: string;
        code?: string;
        /** graphql-java: ValidationError / InvalidSyntax / DataFetchingException / … */
        classification?: string;
      };
    }[];
  };

  if (data.errors?.length) {
    if (!options?.silent) {
      console.error(data.errors);
    }
    const error = data.errors[0];
    const errorCode = error?.extensions?.errorCode ?? error?.extensions?.code;

    // Документ не сошёлся со схемой шлюза: отклонён ЦЕЛИКОМ, ещё до резолверов, с HTTP 200 и
    // без `errorCode`. Раньше это доходило до общего throw ниже и человек видел безликое
    // «что-то пошло не так» — ровно так выглядела авария versionCode 11. Помечаем отдельным
    // кодом; что именно показать (обновиться или «попробуйте позже»), решает queryClient по
    // версии из /app/status.json, а не по одному этому признаку.
    if (isSchemaDriftError(error)) {
      throw new ApiError(error?.message ?? 'Client out of date', res.status, CLIENT_OUTDATED_CODE);
    }

    // Partial success: the operation resolved, but a nested nullable auth-gated field
    // (e.g. discordVoiceChannel.realtimeMembers) came back `user.not.auth`. GraphQL nulls
    // just that field and still fills the rest — so don't throw a fully valid post away
    // over a sub-field the caller already treats as optional. Scoped tight: only when data
    // is present AND every error is a not-logged-in error on a nested field (path depth ≥ 3
    // in this API's `wrapper { resolver { field } }` shape). A root-level auth failure
    // (shorter path or null data) still throws below, so login redirects are unaffected;
    // banned / permission / other errors are never swallowed.
    const isRecoverableFieldAuthMiss = (candidate: NonNullable<typeof data.errors>[number]) => {
      const code = candidate?.extensions?.errorCode ?? candidate?.extensions?.code;
      const isNestedField = (candidate?.path?.length ?? 0) >= 3;
      return (
        isNestedField && (code === USER_NOT_AUTH_CODE || candidate?.extensions?.httpCode === 401)
      );
    };
    if (data.data != null && data.errors.every(isRecoverableFieldAuthMiss)) {
      return data.data;
    }

    if (error?.extensions?.httpCode === 401 || errorCode === USER_NOT_AUTH_CODE) {
      // Tagged so callers can tell "our gateway says this token is no good" apart from a
      // bare 401/403 that some layer in front of us produced — see the throw below.
      throw new ApiError('Unauthorized', 401, USER_NOT_AUTH_CODE);
    }

    if (error && errorCode === USER_BANNED_CODE) {
      // The gateway has already reset the auth cookie; do a full-page redirect so the
      // app restarts in the anonymous state on the static /banned page.
      if (envConfig.IN_BROWSER) {
        // Срок и причина приезжают в самом отказе: другого случая их узнать не будет —
        // страница /banned открывается анонимной и спросить ей уже не у кого.
        rememberBanNotice(error.extensions as Record<string, unknown> | undefined);
        window.location.href = '/banned';
      }

      throw new ApiError(error.message, 403, errorCode);
    }

    if (error?.extensions?.httpCode === 403 || errorCode === PERMISSION_DENIED_CODE) {
      throw new ApiError('Access forbidden', res.status);
    }

    if (error) {
      throw new ApiError(error.message, res.status, errorCode);
    }
  }

  return data.data;
};
